import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  useTheme,
  Stack,
  CircularProgress
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../Api/instance";
import Footer from "../footer";

// Assets imports
import PhoneIconGif from "../../asserts/system-solid-58-call-phone.gif";
import EmailIconGif from "../../asserts/system-solid-59-email.gif";
import LocationIconGif from "../../asserts/wired-flat-18-location-pin.gif";
import MessageIconGif from "../../asserts/wired-outline-981-consultation.gif";
import PartyIconGif from "../../asserts/wired-outline-1103-confetti.gif";

export default function ContactUs() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const Navigate = useNavigate();

  // Controlled form states (initialized to empty strings to avoid uncontrolled warning)
  const [name, setName] = useState("");
  const [mail, setMail] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const email = localStorage.getItem("userMail");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`get/U_ViewStaticInfo.php`, {
        userId: email,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          Navigate("/signin");
          return;
        }
        setData(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching contact info static data:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendMail = async (toEmail, subject, bodyMessage) => {
    const encodedSubject = encodeURIComponent(subject);
    const encodedMessage = encodeURIComponent(bodyMessage);
    const url = `https://vebbox.in/mail/index.php?to_mail=nursingupdate2@gmail.com&subject=${encodedSubject}&msg=${encodedMessage}`;

    try {
      const response = await axiosInstance.get(url);
      if (response.status === 200) {
        console.log("Mail notification sent successfully");
      } else {
        console.log("Failed to send mail notification");
      }
    } catch (error) {
      console.error("Error sending mail notification:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !mail || !contact || !message) {
      alert("Please fill out all required fields marked with *");
      return;
    }

    try {
      await sendMail(
        mail,
        "New Contact Form Submission",
        `Name: ${name}\nEmail: ${mail}\nContact: ${contact}\nMessage: ${message}`
      );

      alert("Message sent successfully!");
      setName("");
      setMail("");
      setContact("");
      setMessage("");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      alert("Failed to send message.");
    }
  };

  // Safe fallback info in case API is empty or fails
  const contactInfo = data[0] || {
    mobibleno: "+91 90424 02446",
    gmaillink: "nursingupdate2@gmail.com",
    address: "Venba Coaching Institute, Chennai, Tamil Nadu, India"
  };

  // Modern container styling
  const containerBg = isDark 
    ? 'linear-gradient(135deg, #0b0f19 0%, #111827 100%)' 
    : 'linear-gradient(135deg, #EEF5FF 0%, #F5F7FB 100%)';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', transition: 'background-color 0.3s ease' }}>
      
      <Box sx={{ background: containerBg, py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="stretch">
            
            {/* Left Column: Contact Information */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  height: '100%',
                  borderRadius: '24px',
                  bgcolor: 'background.paper',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: isDark ? '0 12px 36px rgba(0, 0, 0, 0.4)' : '0 12px 36px rgba(15, 23, 42, 0.05)',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>
                      Contact Us
                    </Typography>
                    <Box 
                      sx={{ 
                        width: '50px', 
                        height: '50px', 
                        borderRadius: '12px', 
                        bgcolor: isDark ? 'rgba(96, 165, 250, 0.1)' : 'rgba(30, 58, 138, 0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img src={MessageIconGif} height="36px" width="36px" alt="" style={{ objectFit: 'contain' }} />
                    </Box>
                  </Stack>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 5, lineHeight: 1.6 }}>
                    Let us know your queries, feedback, or enquiries. We are here to support you 24/7.
                  </Typography>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress color="primary" />
                    </Box>
                  ) : (
                    <Stack spacing={4}>
                      
                      {/* Phone Info */}
                      <Stack direction="row" spacing={3} alignItems="center" sx={{ p: 1 }}>
                        <Box
                          sx={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '16px',
                            bgcolor: isDark ? 'rgba(52, 211, 153, 0.1)' : 'rgba(5, 150, 105, 0.06)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.08)' }
                          }}
                        >
                          <img src={PhoneIconGif} height="36px" width="36px" alt="" />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Give us a call
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.5 }}>
                            {contactInfo.mobibleno}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Email Info */}
                      <Stack direction="row" spacing={3} alignItems="center" sx={{ p: 1 }}>
                        <Box
                          sx={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '16px',
                            bgcolor: isDark ? 'rgba(96, 165, 250, 0.1)' : 'rgba(37, 99, 235, 0.06)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.08)' }
                          }}
                        >
                          <img src={EmailIconGif} height="36px" width="36px" alt="" />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Write to us
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.5, wordBreak: 'break-all' }}>
                            {contactInfo.gmaillink}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Address Info */}
                      <Stack direction="row" spacing={3} alignItems="center" sx={{ p: 1 }}>
                        <Box
                          sx={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '16px',
                            bgcolor: isDark ? 'rgba(251, 191, 36, 0.1)' : 'rgba(217, 119, 6, 0.06)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.08)' }
                          }}
                        >
                          <img src={LocationIconGif} height="36px" width="36px" alt="" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Find Us
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.5, lineHeight: 1.5 }}>
                            {contactInfo.address}
                          </Typography>
                        </Box>
                      </Stack>

                    </Stack>
                  )}
                </Box>
                
                {/* Party celebration footer decoration */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                  <img src={PartyIconGif} height="64px" width="64px" alt="" style={{ opacity: 0.8 }} />
                </Box>
              </Paper>
            </Grid>

            {/* Right Column: Contact Form */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  height: '100%',
                  borderRadius: '24px',
                  bgcolor: 'background.paper',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  boxShadow: isDark ? '0 12px 36px rgba(0, 0, 0, 0.4)' : '0 12px 36px rgba(15, 23, 42, 0.05)',
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: 'primary.main' }}>
                    Say Hello!
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Feel free to stop by and say hi! We value your feedback.
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    required
                    label="Name"
                    value={name}
                    variant="outlined"
                    fullWidth
                    onChange={(e) => setName(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      }
                    }}
                  />

                  <TextField
                    required
                    label="Email"
                    type="email"
                    value={mail}
                    variant="outlined"
                    fullWidth
                    onChange={(e) => setMail(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      }
                    }}
                  />

                  <TextField
                    required
                    label="Contact Number"
                    type="number"
                    value={contact}
                    variant="outlined"
                    fullWidth
                    onChange={(e) => setContact(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      }
                    }}
                  />

                  <TextField
                    required
                    label="Message"
                    value={message}
                    variant="outlined"
                    multiline
                    rows={4}
                    fullWidth
                    onChange={(e) => setMessage(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      py: 1.8,
                      mt: 1,
                      borderRadius: '14px',
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #F0A04B 0%, #D97706 100%)',
                      boxShadow: '0 4px 14px rgba(240, 160, 75, 0.4)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(217, 119, 6, 0.5)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      }
                    }}
                  >
                    Send Message
                  </Button>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
